import { Application } from "@core/Application";
import { ColaboratorInfo } from "@models/Colaborators";
import { AuditChange, AuditLog } from "@services/AuditService";
import { useCallback, useEffect, useMemo, useState } from "react";

interface UseColaboradorAuditoriaViewModelArgs {
    orgID: string;
    colaborador?: Partial<ColaboratorInfo> | null;
}

export interface CollaboratorAuditTableRow {
    id: string;
    action: string;
    fecha: string;
    hora: string;
    tipo: string;
    responsable: string;
    change?: AuditChange;
}

const buildAuditTableRows = (auditLogs: AuditLog[]): CollaboratorAuditTableRow[] => {
    return auditLogs.flatMap((auditLog) => {
        if (auditLog.action === "edit" && auditLog.changes?.length) {
            return auditLog.changes.map((change, index) => ({
                ...auditLog,
                id: `${auditLog.id}-${change.field}-${index}`,
                change,
            }));
        }

        return [{ ...auditLog, change: undefined }];
    });
};

export function useColaboradorAuditoriaViewModel({
    orgID,
    colaborador,
}: UseColaboradorAuditoriaViewModelArgs) {
    const [auditData, setAuditData] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");

    const fetchAuditLogs = useCallback(async () => {
        if (!colaborador?.id || !orgID) {
            setAuditData([]);
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const auditService = Application.shared().coreDependencies.auditService;
            const result = await auditService.getAuditLogs(colaborador.id, orgID);
            setAuditData(result);
        } catch (error) {
            console.error("Error fetching collaborator audit logs:", error);
            setAuditData([]);
            setError("No se pudieron cargar los registros de auditoria.");
        } finally {
            setLoading(false);
        }
    }, [colaborador?.id, orgID]);

    useEffect(() => {
        void fetchAuditLogs();
    }, [fetchAuditLogs]);

    const auditRows = useMemo(() => buildAuditTableRows(auditData), [auditData]);

    return {
        auditRows,
        searchQuery,
        setSearchQuery,
        loading,
        error,
    };
}
